import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Linkedin, Globe, ArrowRight, CheckCircle, Zap } from 'lucide-react';
import LiveDate from '../components/LiveDate';
import './About.css';

const EXPERTISE = [
  { area: 'Answer Engine Optimization (AEO)', years: '3+', level: 95 },
  { area: 'Generative Engine Optimization (GEO)', years: '2+', level: 92 },
  { area: 'Traditional SEO & SEM', years: '12+', level: 99 },
  { area: 'Google Ads Management', years: '10+', level: 97 },
  { area: 'WordPress Development', years: '12+', level: 98 },
  { area: 'LLM & AI Platform Optimization', years: '2+', level: 88 },
  { area: 'AI Content Strategy', years: '3+', level: 90 },
  { area: 'Digital Marketing Strategy', years: '12+', level: 99 },
];

const TIMELINE = [
  { year: '2012', title: 'Digital Journey Begins', desc: 'Started career in digital marketing and web development, building expertise in WordPress and SEO.' },
  { year: '2015', title: 'SEO Mastery', desc: 'Became a recognized SEO expert in Kerala, achieving first-page rankings across competitive industries.' },
  { year: '2018', title: 'Google Ads Expert', desc: 'Managed ₹6.5 Cr+ in Google Ads spend, delivering measurable ROI for businesses across India and globally.' },
  { year: '2020', title: 'Digital Strategy Consulting', desc: 'Expanded into full IT consulting and digital transformation for businesses in 15+ countries.' },
  { year: '2022', title: 'AI Transition Pioneer', desc: 'Recognized the AI search revolution early. Began developing AEO and GEO strategies before mainstream adoption.' },
  { year: '2023', title: 'First AEO Expert in Kerala', desc: 'Established Kerala\'s first dedicated Answer Engine Optimization practice. Featured in industry publications.' },
  { year: '2024', title: 'AI Optimization Suite', desc: 'Launched comprehensive AI optimization services covering GEO, LLMO, GAIO, AISO, and 10+ more disciplines.' },
  { year: '2025', title: 'Kerala\'s AI Visibility Leader', desc: 'Recognized as the leading AI Platform Optimization expert in Kerala with 150+ successful AI-optimized businesses.' },
];

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Rajesh R | Kerala's #1 AEO & AI Optimization Expert | 12+ Years Experience</title>
        <meta name="description" content="Meet Rajesh R — Kerala's leading Answer Engine Optimization and AI Platform expert. 12+ years experience, ₹6.5 Cr+ Google Ads managed, 2450+ happy clients. Based in Trivandrum, Kerala." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Rajesh R",
          "jobTitle": "AI Platform Optimization Expert & AEO Specialist",
          "description": "Kerala's leading Answer Engine Optimization and AI visibility expert with 12+ years of digital marketing experience",
          "url": "https://rajeshrnair.com",
          "telephone": "+917907038984",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Thiruvananthapuram",
            "addressRegion": "Kerala",
            "addressCountry": "IN"
          },
          "sameAs": [
            "https://rajeshrnair.com",
            "https://www.linkedin.com/in/rajesh8129355259/"
          ],
          "knowsAbout": ["AEO", "GEO", "SEO", "AI Optimization", "Digital Marketing", "WordPress", "Google Ads", "LLM Optimization"]
        })}</script>
      </Helmet>

      <div className="page-hero" style={{ paddingBottom: '80px' }}>
        <div className="container">
          <motion.div className="about-hero-grid" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div>
              <div className="sp-live-bar">
                <span className="live-dot"></span>
                <LiveDate showTime className="hero-live-date" />
              </div>
              <div className="badge" style={{ marginBottom: '16px' }}><Award size={12} /> Kerala's AI Optimization Pioneer</div>
              <h1 className="sp-title">Meet <span className="gradient-text">Rajesh R</span></h1>
              <p className="sp-tagline">AI Platform Optimization Expert | AEO Specialist | 12+ Years Digital Mastery</p>
              <p className="section-subtitle" style={{ marginTop: '16px' }}>
                I'm Kerala's first dedicated Answer Engine Optimization and Generative AI visibility expert —
                helping businesses get found not just on Google, but inside ChatGPT, Gemini, Perplexity, and
                every AI platform shaping the future of search.
              </p>
              <div className="about-stats-row">
                {[
                  { val: '12+', lab: 'Years Experience' },
                  { val: '2450+', lab: 'Happy Clients' },
                  { val: '₹6.5Cr+', lab: 'Google Ads Managed' },
                  { val: '15+', lab: 'Countries Served' },
                ].map(s => (
                  <div key={s.lab} className="about-stat">
                    <div className="stat-number" style={{ fontSize: '28px' }}>{s.val}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.lab}</div>
                  </div>
                ))}
              </div>
              <div className="about-ctas">
                <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20read%20your%20About%20page%20and%20I%E2%80%99m%20impressed%20with%20your%2012%2B%20years%20of%20experience.%20I%E2%80%99d%20like%20to%20work%20with%20you%20on%20AI%20optimization%20for%20my%20business." target="_blank" rel="noreferrer" className="btn-primary">
                  <Zap size={16} /> Work With Me
                </a>
                <a href="https://www.linkedin.com/in/rajesh8129355259/" target="_blank" rel="noreferrer" className="btn-secondary">
                  <Linkedin size={16} /> LinkedIn Profile
                </a>
                <a href="https://rajeshrnair.com" target="_blank" rel="noreferrer" className="btn-secondary">
                  <Globe size={16} /> Website
                </a>
              </div>
            </div>
            <div className="about-profile-visual">
              <div className="profile-card">
                <div className="profile-avatar">
                  <span>RR</span>
                </div>
                <h3>Rajesh R</h3>
                <p>AI Platform Optimization Expert</p>
                <div className="profile-tags">
                  {['AEO', 'GEO', 'LLMO', 'SEO', 'Google Ads', 'WordPress', 'AI Strategy'].map(t => (
                    <span key={t} className="profile-tag">{t}</span>
                  ))}
                </div>
                <div className="profile-contact">
                  <a href="tel:+917907038984">📞 +91 79070 38984</a>
                  <a href="https://rajeshrnair.com" target="_blank" rel="noreferrer">🌐 rajeshrnair.com</a>
                  <span>📍 Trivandrum, Kerala</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* EXPERTISE */}
      <section className="about-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}>Skills & Expertise</div>
            <h2 className="section-title">Areas of <span className="gradient-text">Deep Expertise</span></h2>
          </motion.div>
          <div className="expertise-grid">
            {EXPERTISE.map((e, i) => (
              <motion.div key={e.area} className="expertise-item"
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }} viewport={{ once: true }}>
                <div className="expertise-header">
                  <span className="expertise-name">{e.area}</span>
                  <span className="expertise-years">{e.years} yrs — {e.level}%</span>
                </div>
                <div className="expertise-bar-track">
                  <motion.div className="expertise-bar-fill"
                    initial={{ width: 0 }} whileInView={{ width: `${e.level}%` }}
                    transition={{ duration: 1.2, delay: i * 0.06 }} viewport={{ once: true }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="about-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge" style={{ marginBottom: '16px' }}>Journey</div>
            <h2 className="section-title">12+ Years of <span className="gradient-text">Digital Evolution</span></h2>
          </motion.div>
          <div className="timeline">
            {TIMELINE.map((t, i) => (
              <motion.div key={t.year} className="timeline-item"
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="timeline-year">{t.year}</div>
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY I DO THIS */}
      <section className="about-section">
        <div className="container">
          <div className="about-mission-grid">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="badge" style={{ marginBottom: '16px' }}>My Mission</div>
              <h2 className="section-title">Why I Built <span className="gradient-text">Kerala's First AEO Practice</span></h2>
              <div className="mission-content">
                <p>In 2022, I saw what most digital marketers in India hadn't noticed yet: Google was losing ground to AI. ChatGPT launched and changed everything. Suddenly, millions of people were getting answers from AI instead of searching Google.</p>
                <p>Kerala's businesses — the ones I'd spent a decade helping rank on Google — were about to become invisible to a new generation of AI-first users. I couldn't let that happen.</p>
                <p>I spent months researching how ChatGPT, Gemini, and Perplexity decide what to recommend. I developed the AEO and GEO frameworks that are now driving AI visibility for 150+ Kerala businesses.</p>
                <p>My mission is simple: <strong>no Kerala business should be invisible to AI.</strong> Whether you're a restaurant in Kochi, a doctor in Thrissur, or an IT firm in Trivandrum — your customers are asking AI about you. I make sure AI has the right answer.</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '24px' }}>
                {[
                  'First dedicated AEO practitioner in Kerala',
                  '150+ businesses AI-optimized and growing',
                  'Constantly learning and adapting to new AI platforms',
                  'Transparent, results-driven approach',
                ].map(p => (
                  <div key={p} style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '14px' }}>
                    <CheckCircle size={16} color="var(--accent-green)" />
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="testimonials">
                {[
                  { name: 'Jobin Thomas', role: 'Founder', text: 'Rajesh is one of the best people I had as a developer. His ability to tackle any problem is remarkable. I highly recommend his expertise to any person or company looking for an expert WordPress developer or SEO expert.' },
                  { name: 'Micheal Antony', role: 'UI/UX Designer', text: 'The skill and ability of Rajesh in doing WordPress and Bootstrap projects is remarkable. He can handle any kind of website development works within limited time and high quality.' },
                  { name: 'Rahim', role: 'Marketing Manager', text: 'Rajesh is a good listener. An expert in SEO, he can bring any web page on digital space to the first page. Rajesh is an asset to any organisation who want to optimize their websites.' },
                ].map(t => (
                  <div key={t.name} className="testimonial-card">
                    <div className="test-stars">⭐⭐⭐⭐⭐</div>
                    <p className="test-text">"{t.text}"</p>
                    <div className="test-author">
                      <strong>{t.name}</strong>
                      <span>{t.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="about-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="section-title">Ready to Work Together?</h2>
            <p className="section-subtitle" style={{ margin: '16px auto 32px' }}>
              Let's discuss how I can help your business become visible in the AI-powered future of search.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">Get Free Consultation <ArrowRight size={16} /></Link>
              <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20went%20through%20your%20profile%20and%20I%E2%80%99d%20love%20to%20schedule%20a%20free%20consultation%20for%20AI%20visibility%20strategy." target="_blank" rel="noreferrer" className="btn-secondary">WhatsApp Me</a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
